/**
 * Ранг выделения (ADR 0010): единственное место, где решается «кто кого
 * перебивает». safety > wayfinding > promo — числовые диапазоны гарантируют,
 * что реклама арифметически НЕ догонит укрытие ни при каких данных.
 * Общая формула для 2D-карты и 3D-«Объёма» (движки не должны разойтись).
 */

/** Роль безопасной точки — задаёт базовый ранг внутри диапазона safety. */
export type SafetyKind =
  | 'shelter'
  | 'assembly'
  | 'muster'
  | 'aed'
  | 'first_aid'
  | 'fire_exit'
  | 'fire_equipment'

export type WayfindingKind = 'selected' | 'you_are_here' | 'level_link'
export type PromoTier = 'featured' | 'promo'

/** Состояние машины экрана — гасит промо на маршруте и в аварии. */
export type EmphasisPhase = 'overview' | 'target' | 'route' | 'follow' | 'emergency'

const SAFETY_RANK: Readonly<Record<SafetyKind, number>> = {
  shelter: 960,
  assembly: 960,
  muster: 960,
  aed: 940,
  first_aid: 940,
  fire_exit: 920,
  fire_equipment: 900,
}

const WAYFINDING_RANK: Readonly<Record<WayfindingKind, number>> = {
  selected: 580,
  you_are_here: 560,
  level_link: 520,
}

export const SAFETY_MIN = 900
export const SAFETY_MAX = 999
export const PROMO_MIN = 100
export const PROMO_MAX = 299

const clampRange = (v: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, v))

/** Ранг безопасной точки (в аварии поднимается ещё выше — поверх всего). */
export function safetyRank(kind: SafetyKind, phase: EmphasisPhase = 'overview'): number {
  const base = SAFETY_RANK[kind]
  return phase === 'emergency' ? clampRange(base + 30, SAFETY_MIN, SAFETY_MAX) : base
}

export function wayfindingRank(kind: WayfindingKind): number {
  return WAYFINDING_RANK[kind]
}

/**
 * Ранг промо/featured. Гаснет (→0) в состояниях МАРШРУТ/ДВИЖЕНИЕ/АВАРИЯ —
 * реклама никогда поперёк маршрута и исчезает в кризис. Рантайм-клампинг в
 * [100,299] — defense in depth, даже если данные обошли линтер.
 */
export function promoRank(tier: PromoTier, phase: EmphasisPhase): number {
  if (phase === 'route' || phase === 'follow' || phase === 'emergency') return 0
  const base = tier === 'featured' ? 250 : 150
  return clampRange(base, PROMO_MIN, PROMO_MAX)
}
